import { useMemo, useState } from "react";
import { useBookingOrder } from "@/components/hooks/use-booking-order";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import { ActionNavigation } from "@/components/shared/booking/action-navigation";
import { AirconDisclaimerModal } from "@/components/shared/booking/aircon-disclaimer-modal";
import { DeepCleaningDisclaimerModal } from "@/components/shared/booking/deep-cleaning-tip-card";
import { NativeActionNavigation } from "@/components/shared/booking/native-action-navigation";
import { BookingOrderCard } from "@/components/shared/booking/order-card";
import { BookingNavigationBottom } from "@/components/shared/booking/order-card/BookingNavigationBottom";
import { WhatsappSupportBanner } from "@/components/shared/whatsapp-support-banner";
import { View } from "@/components/ui";
import { showToast } from "@/components/ui/toast/show-toast";
import { getHomeBeautyPackageFromCode } from "@/constants/booking";
import { getPlatform } from "@/lib/utils";
import { useBookingState } from "@/store/booking/useBookingState";
import type { ServiceName } from "@/types/service";
import { UpholsteryServiceNames } from "@/types/service";
import { generateBookingChatSalesMessage, onChatSales } from "@/lib/chat-sales";
import { onChatSupport } from "@/lib/chat-support";

export const BookingNavigation = ({ name }: { name: ServiceName }) => {
  const [isDeepCleaningAcknowledgement, setIsDeepCleaningAcknowledgement] =
    useState(false);
  const [isAirconAcknowledgement, setIsAirconAcknowledgement] = useState(false);
  const {
    data: {
      selectedPackageIds,
      homeBeautyFreeGelServiceIds,
      packageDetails,
      petProfileInfo,
      elderCareProfileInfo,
      // babyProfileInfo,
    },
    setBookingState,
  } = useBookingState();
  const { totalBeforeDiscount } = useBookingOrder();
  const { forward } = useBookingRoute();
  const platform = getPlatform();
  const isOrderNavigation = useMemo(
    () =>
      [
        ...UpholsteryServiceNames,
        "aircon-service",
        "home-beauty",
        "home-massage",
        "pet-grooming",
        "pet-sitting",
        "lashes",
        "deep-cleaning",
        "elder-care",
      ].includes(name),
    [name],
  );

  const disabled = useMemo(() => {
    const isCurtainBooking = name === "curtain-cleaning";
    const isHomeBeautyBooking = name === "home-beauty";
    const isPetSitting = name === "pet-sitting";

    if (isCurtainBooking && totalBeforeDiscount < 100) {
      return true;
    }

    if (isPetSitting && !petProfileInfo) {
      return true;
    }

    if (isOrderNavigation) {
      // make sure enough amount of service is selected for freeGelRemoval
      if (isHomeBeautyBooking) {
        if (homeBeautyFreeGelServiceIds.length) {
          const arr = homeBeautyFreeGelServiceIds.map((id) => {
            const code = packageDetails.find((pkg) => pkg.id === id)
              ?.code as string;
            const pkg = getHomeBeautyPackageFromCode(code);
            return (
              selectedPackageIds.filter((selectedId) => selectedId === id)
                .length >= (pkg.minFreeGelAmount || 1000)
            );
          });
          if (arr.indexOf(false) !== -1) return true;
        }
      }
      return !selectedPackageIds.length;
    }
    return false;
  }, [name, selectedPackageIds.length, petProfileInfo]);

  const handlePetGroomingProceed = () => {
    if (petProfileInfo) {
      forward();
    } else {
      showToast({
        type: "error",
        title: "Please select a pet profile to continue",
      });
    }
  };

  const handlePetSittingProceed = () => {
    if (petProfileInfo) {
      forward();
    }
  };

  const handleElderCareProceed = () => {
    if (elderCareProfileInfo) {
      forward();
    } else {
      setBookingState({
        isEmptyErrorElderProfile: true,
      });
      showToast({
        type: "error",
        title: "Please select an elder care profile to continue",
      });
    }
  };

  // still un hold pending on vertical lead babysitter
  // const handleBabysitterProceed = () => {
  //   if (babyProfileInfo) {
  //     setBookingState({ babysitterDurationModal: true });
  //   } else {
  //     showToast({
  //       type: "error",
  //       title: "Please select a baby profile to continue",
  //     });
  //   }
  // };

  const onClickButton = () => {
    switch (name) {
      case "home-cleaning":
        setBookingState({ serviceDurationModalOpen: true });
        break;

      case "pet-grooming":
        handlePetGroomingProceed();
        break;

      case "pet-sitting":
        handlePetSittingProceed();
        break;

      case "deep-cleaning":
        setIsDeepCleaningAcknowledgement(true);
        break;
      case "aircon-service":
        setIsAirconAcknowledgement(true);
        break;

      case "elder-care":
        handleElderCareProceed();
        break;

      // still un hold pending on vertical lead babysitter
      // case "babysitter":
      //   handleBabysitterProceed();
      //   break;

      default:
        forward();
    }
  };

  if (isOrderNavigation) {
    return (
      <View>
        {/* large screen */}
        {platform === "web" && (
          <View className="hidden flex-col gap-4 md:flex">
            <BookingOrderCard
              buttonText="Proceed"
              onContinue={onClickButton}
              disabled={disabled}
              loading={false}
            />
            <WhatsappSupportBanner
              onClick={() =>
                onChatSales(true, generateBookingChatSalesMessage(name))
              }
            />
          </View>
        )}

        {/* narrow screen */}
        <BookingNavigationBottom
          name={name}
          buttonText="Proceed"
          showExtendIcon={!!selectedPackageIds.length}
          onContinue={onClickButton}
          disabled={disabled}
        />
        <DeepCleaningDisclaimerModal
          isOpen={isDeepCleaningAcknowledgement}
          onAcknowledge={() => setIsDeepCleaningAcknowledgement(false)}
        />
        <AirconDisclaimerModal
          isOpen={isAirconAcknowledgement}
          onAcknowledge={() => setIsAirconAcknowledgement(false)}
        />
      </View>
    );
  }

  return (
    <View>
      <View className="flex flex-col gap-4">
        {/* large screen */}
        <ActionNavigation buttonText="Proceed" onClickButton={onClickButton} />
        <View className="hidden md:block">
          <WhatsappSupportBanner
            onClick={() => onChatSupport(generateBookingChatSalesMessage(name))}
          />
        </View>
      </View>
      {/* narrow screen */}
      <NativeActionNavigation
        name={name}
        buttonText="Proceed"
        onClickButton={onClickButton}
      />
    </View>
  );
};
